import {
  Button,
  Drawer,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconLink,
  IconTimeline,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { JournalTimeline, type JournalEntry } from "@/components/JournalTimeline";
import { SubmissionLinksManager } from "@/components/SubmissionLinksManager";
import { useCreateJournalEntry, useJournalEntry } from "@/hooks/useJournal";
import { getAutoWeather } from "@/lib/weather";

export const Route = createFileRoute("/projects/$projectId/journal" as any)({
  component: JournalPage,
});

function JournalPage() {
  const params = Route.useParams();
  const projectId = "projectId" in params ? params.projectId : "";

  const [activeTab, setActiveTab] = useState<string | null>("timeline");
  const [createModalOpened, createModalHandlers] = useDisclosure(false);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);

  const handleViewEntry = (entry: JournalEntry) => {
    setSelectedEntry(entry);
  };

  return (
    <Paper p="xl" withBorder>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <div>
            <Title order={2}>Project Journal</Title>
            <Text size="sm" c="dimmed">
              Unified timeline of all project activity
            </Text>
          </div>
        </Group>

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="timeline" leftSection={<IconTimeline size={16} />}>
              Timeline
            </Tabs.Tab>
            <Tabs.Tab value="links" leftSection={<IconLink size={16} />}>
              Submission Links
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="timeline" pt="md">
            <JournalTimeline
              projectId={projectId}
              onCreateEntry={createModalHandlers.open}
              onViewEntry={handleViewEntry}
            />
          </Tabs.Panel>

          <Tabs.Panel value="links" pt="md">
            <SubmissionLinksManager projectId={projectId} />
          </Tabs.Panel>
        </Tabs>
      </Stack>

      {/* Create Entry Modal */}
      <CreateEntryModal
        opened={createModalOpened}
        onClose={createModalHandlers.close}
        projectId={projectId}
      />

      {/* Entry Detail Drawer */}
      <Drawer
        opened={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
        title={selectedEntry?.title || selectedEntry?.referenceNumber || "Entry Details"}
        position="right"
        size="lg"
      >
        {selectedEntry && (
          <EntryDetail entry={selectedEntry} projectId={projectId} />
        )}
      </Drawer>
    </Paper>
  );
}

interface CreateEntryModalProps {
  opened: boolean;
  onClose: () => void;
  projectId: string;
}

function CreateEntryModal({ opened, onClose, projectId }: CreateEntryModalProps) {
  const createEntry = useCreateJournalEntry(projectId);
  const [weatherLoading, setWeatherLoading] = useState(false);

  const form = useForm({
    initialValues: {
      entryType: "log" as "log" | "note" | "checkpoint" | "update",
      title: "",
      content: "",
      category: "",
      weatherTemp: null as number | null,
      weatherConditions: "",
      crewCount: null as number | null,
      hasIssue: false,
    },
    validate: {
      content: (value) => (value.length < 1 ? "Content is required" : null),
    },
  });

  const fetchWeather = async () => {
    setWeatherLoading(true);
    try {
      const weather = await getAutoWeather();
      if (weather) {
        form.setFieldValue("weatherTemp", weather.temperature);
        form.setFieldValue("weatherConditions", weather.conditions);
      }
    } catch (error) {
      console.warn("Failed to fetch weather:", error);
    } finally {
      setWeatherLoading(false);
    }
  };

  const handleSubmit = form.onSubmit(async (values) => {
    try {
      await createEntry.mutateAsync({
        entryType: values.entryType,
        title: values.title || undefined,
        content: values.content,
        category: values.category || undefined,
        weatherTemp: values.weatherTemp || undefined,
        weatherConditions: values.weatherConditions || undefined,
        crewCount: values.crewCount || undefined,
        hasIssue: values.hasIssue,
      });

      notifications.show({
        title: "Entry created",
        message: "Your journal entry has been added",
        color: "green",
      });

      form.reset();
      onClose();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to create entry",
        color: "red",
      });
    }
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Add Journal Entry" size="lg">
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Select
            label="Entry Type"
            data={[
              { value: "log", label: "Work Log" },
              { value: "note", label: "Note" },
              { value: "checkpoint", label: "Checkpoint" },
              { value: "update", label: "Update" },
            ]}
            {...form.getInputProps("entryType")}
          />

          <TextInput
            label="Title"
            placeholder="Optional title for this entry"
            {...form.getInputProps("title")}
          />

          <Textarea
            label="Content"
            placeholder="What happened? What was accomplished?"
            minRows={4}
            {...form.getInputProps("content")}
            required
          />

          <Select
            label="Category"
            placeholder="Select category"
            clearable
            data={[
              { value: "work", label: "Work" },
              { value: "checkpoint", label: "Checkpoint" },
              { value: "issue", label: "Issue" },
              { value: "delivery", label: "Delivery" },
              { value: "general", label: "General" },
            ]}
            {...form.getInputProps("category")}
          />

          <Group grow>
            <NumberInput
              label="Temperature (°F)"
              placeholder="e.g., 72"
              {...form.getInputProps("weatherTemp")}
              rightSection={
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={fetchWeather}
                  loading={weatherLoading}
                >
                  Auto
                </Button>
              }
            />
            <Select
              label="Weather Conditions"
              placeholder="Select conditions"
              clearable
              data={[
                { value: "Clear", label: "Clear" },
                { value: "Partly Cloudy", label: "Partly Cloudy" },
                { value: "Cloudy", label: "Cloudy" },
                { value: "Rain", label: "Rain" },
                { value: "Snow", label: "Snow" },
                { value: "Windy", label: "Windy" },
              ]}
              {...form.getInputProps("weatherConditions")}
            />
          </Group>

          <NumberInput
            label="Crew Count"
            placeholder="Number of crew on site"
            min={0}
            {...form.getInputProps("crewCount")}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={createEntry.isPending}>
              Create Entry
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

interface EntryDetailProps {
  entry: JournalEntry;
  projectId: string;
}

function EntryDetail({ entry, projectId }: EntryDetailProps) {
  // Fetch entry with replies if it has replies
  const { data: entryData } = useJournalEntry(
    projectId,
    entry.replyCount > 0 ? entry.id : ""
  );
  const replies = entryData?.replies || [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatReplyDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <Stack gap="md">
      {/* Metadata */}
      <div>
        <Text size="sm" c="dimmed">
          {formatDate(entry.createdAt)}
        </Text>
        {entry.referenceNumber && (
          <Text size="sm" c="dimmed">
            Reference: {entry.referenceNumber}
          </Text>
        )}
      </div>

      {/* Author */}
      <div>
        <Text size="sm" fw={600}>
          {entry.source === "internal"
            ? entry.createdByUser?.name || "Team Member"
            : entry.submitterName || "External Contributor"}
        </Text>
        {entry.source === "link" && entry.sourceLink && (
          <Text size="xs" c="dimmed">
            via {entry.sourceLink.name}
          </Text>
        )}
      </div>

      {/* Title */}
      {entry.title && (
        <Title order={4}>{entry.title}</Title>
      )}

      {/* Content */}
      <Text style={{ whiteSpace: "pre-wrap" }}>{entry.content}</Text>

      {/* Context */}
      {(entry.weatherTemp || entry.crewCount) && (
        <Paper p="sm" withBorder>
          <Group gap="xl">
            {entry.weatherTemp && (
              <div>
                <Text size="xs" c="dimmed">Weather</Text>
                <Text size="sm">
                  {entry.weatherTemp}°F
                  {entry.weatherConditions && ` - ${entry.weatherConditions}`}
                </Text>
              </div>
            )}
            {entry.crewCount && (
              <div>
                <Text size="xs" c="dimmed">Crew</Text>
                <Text size="sm">{entry.crewCount} on site</Text>
              </div>
            )}
          </Group>
        </Paper>
      )}

      {/* Photos placeholder */}
      {entry.photos && entry.photos.length > 0 && (
        <div>
          <Text size="sm" fw={600} mb="xs">
            Photos ({entry.photos.length})
          </Text>
          <Text size="sm" c="dimmed">
            Photo display coming soon...
          </Text>
        </div>
      )}

      {/* Thread replies */}
      {entry.replyCount > 0 && (
        <div>
          <Text size="sm" fw={600} mb="xs">
            Replies ({entry.replyCount})
          </Text>
          <Stack gap="sm">
            {replies.length > 0 ? (
              replies.map((reply) => (
                <Paper key={reply.id} p="sm" withBorder bg="gray.0">
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={500}>
                      {reply.source === "internal"
                        ? reply.createdByUser?.name || "Team Member"
                        : reply.submitterName || "External"}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatReplyDate(reply.createdAt)}
                    </Text>
                  </Group>
                  <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                    {reply.content}
                  </Text>
                </Paper>
              ))
            ) : (
              <Text size="sm" c="dimmed">Loading replies...</Text>
            )}
          </Stack>
        </div>
      )}
    </Stack>
  );
}
